import {useContext, useMemo, useState} from "react";
import {DatePicker, Pagination, Select, Table} from "antd";
import dayjs from "dayjs";
import {
	Image,
	Button,
	StatusLabel,
	PopperUserInfo
} from "~/components";
import {
	renderDate,
	numberFormat,
	arrayToMap
} from "~/utils";

import RevenueAction from "./RevenueAction";
import {
	useDevice,
	useGroup,
	useUtilities
} from "~/hooks";
import {AppContext} from "~/context/AppProvider";

const paymentOptions = [
	{ value: 'cod', label: 'Tiền mặt'},
	{ value: 'bank', label: 'Chuyển khoản'},
	{ value: 'momo', label: 'Ví Momo'},
	{ value: 'swipe', label: 'Pos'},
];

function RevenueTable({items = [], filter, pagination, onPaginationChange, onChangeFilter, setItemEdit, openModal, handleStatus, can, onInlineUpdate}) {

	const {tableHeight} = useContext(AppContext);

	const {isMobile} = useDevice();

	const listGroup = useGroup();

	const {utilities} = useUtilities();

	const groupMap = arrayToMap(listGroup);

	const bankAccountOptions = useMemo(() => {
		return Object.entries(utilities.bankAccounts || {}).map(([key, value]) => ({
			value: key,
			label: value.title,
		}));
	}, [utilities.bankAccounts]);

	const [editingCell, setEditingCell] = useState(null); // { id, field }
	const [pendingBank, setPendingBank] = useState(null); // { id } khi chọn bank nhưng chưa chọn tài khoản

	const canEditInline = can?.plus || can?.edit;
	const canEditTimeReal = can?.timeReal;

	const handleInfoClick = (item) => {
		setItemEdit(item);
		openModal('info');
	}

	const handleStatusClick = (item) => {
		setItemEdit(item);
		handleStatus(item);
	}

	const handleEditClick = (item) => {
		setItemEdit(item);
		openModal('addEdit');
	}

	const handlePayClick = (item) => {
		setItemEdit(item);
		openModal('pay');
	}

	const handleDeleteClick = (item) => {
		setItemEdit(item);
		openModal('delete');
	}

	const handleTransferClick = (item) => {
		setItemEdit(item);
		openModal('transfer');
	}

	const handleContractClick = (contractId) => {
		if (!onChangeFilter) return;
		const newFilter = {
			...filter,
			contractId: contractId,
			status: '',
			userId: '',
			service: '',
			page: 1,
		};
		onChangeFilter(newFilter);
	}

	const handleInlineSave = (id, data) => {
		setEditingCell(null);
		setPendingBank(null);
		if (onInlineUpdate) {
			onInlineUpdate(id, data);
		}
	}

	let columns = [
		{ title: 'Hợp đồng', dataIndex: 'code', key: "code", width: '330px', render: (_, item) => (
			<div>
				<Button small onClick={() => handleContractClick(item.contractId)}><b style={{color : 'var(--primary)'}}>HD{item.contractId}</b></Button>
				 {item?.contract.name}
				<p>
					{item?.cardFeeRef > 0 && <StatusLabel small type="purple">Phí thẻ</StatusLabel>}
					{item?.roomSales == 1 && <StatusLabel small type="green">Tính cho phòng</StatusLabel>}
					{item?.roomSales == 0 && <StatusLabel small type="yellow">Không Tính cho phòng</StatusLabel>}
					{item?.roomSales == 2 && <StatusLabel small type="red">Không Tính DS</StatusLabel>}
					{isMobile &&
						<>
							<StatusLabel small type={item.service.badge}>{item.service.label}</StatusLabel>
							&nbsp;<StatusLabel small type={item.service.badge}>{item.serviceType.label}</StatusLabel>
						</>
					}
				</p>
				{isMobile && <p className="mt-1" style={{fontSize:'12px', color:'#ccc'}}>Ngày thu {renderDate(item?.time)}</p>}
			</div>
		)},
		{ title: 'Nhân viên', dataIndex: 'user', key: "user", width: '220px', render: (_, item) => (
			<PopperUserInfo user={item.user}>
				<div className="d-flex gap align-items">
					<div className="avatar"><Image src={item.user?.avatar} /></div>
					<div>
						<b>{item.user?.firstname + ' ' + item.user?.lastname}</b>
						<p>{(item?.groupId !== 0) ? groupMap[item?.groupId]?.name : item.user?.username}</p>
					</div>
				</div>
			</PopperUserInfo>
		)},
		{ title: 'Loại thu', dataIndex: 'service', key: 'service', render:(_, item) => (
			<div>
				<StatusLabel small type={item.service.badge}>{item.service.label}</StatusLabel>
				&nbsp;<StatusLabel small type={item.service.badge}>{item.serviceType.label}</StatusLabel>
			</div>
		)},
		{ title: 'Số tiền', dataIndex: 'price', key: "price", width: '110px', render: (_, item) => (
			<div>
				<p><b>{numberFormat(item?.price)}đ</b></p>
			</div>
		)},
		{ title: 'Hoa hồng', dataIndex: 'percent', key: "percent", width: '110px', render: (_, item) => (
			<div>
				<p><b>{numberFormat(item?.commission)}đ ({item?.percent}%)</b></p>
			</div>
		)},
		{ title: 'Lương', dataIndex: 'pay', key: "pay", width: '110px', render: (_, item) => (
			<div>
				{item?.plus === 'confirm' && <StatusLabel small type="green">{renderDate(item.timePlus, 'month')}</StatusLabel>}
				{item?.plus === 'pending' && <StatusLabel small type="red">Chưa tính</StatusLabel>}
			</div>
		)},
		{ title: 'Ngày thu', dataIndex: 'signing', key: "signing", width: '110px', render: (_, item) => (
			<div><p>{renderDate(item?.time)}</p></div>
		)},
		{ title: 'Ngày thực nhận', dataIndex: 'timeReal', key: "timeReal", width: '160px', render: (_, item) => {
			const isEditing = editingCell?.id === item.id && editingCell?.field === 'timeReal';
			if (canEditTimeReal && isEditing) {
				return (
					<DatePicker
						autoFocus
						size="small"
						defaultValue={item.timeReal ? dayjs(item.timeReal * 1000) : null}
						format="DD/MM/YYYY"
						popupStyle={{zIndex: 10000}}
						onChange={(date) => {
							if (date) {
								handleInlineSave(item.id, { timeReal: Math.round(date.valueOf() / 1000) });
							} else {
								setEditingCell(null);
							}
						}}
						onBlur={() => setEditingCell(null)}
					/>
				);
			}
			return (
				<div
					style={canEditTimeReal ? {cursor: 'pointer', minHeight: '24px'} : {}}
					title={canEditTimeReal ? 'Nhấn để chỉnh sửa' : ''}
					onClick={() => canEditTimeReal && setEditingCell({id: item.id, field: 'timeReal'})}
				>
					{item.timeReal ? renderDate(item.timeReal) : <span style={{color: '#ccc'}}>--</span>}
				</div>
			);
		}},
		{ title: 'Phương thức', dataIndex: 'payment', key: "payment", width: '180px', render: (_, item) => {
			const isEditing = editingCell?.id === item.id && editingCell?.field === 'payment';
			const isSelectingBank = pendingBank?.id === item.id;

			if (canEditInline && isSelectingBank) {
				return (
					<div>
						<p style={{marginBottom: 4}}>Chuyển khoản</p>
						<Select
							autoFocus
							size="small"
							placeholder="Chọn tài khoản"
							options={bankAccountOptions}
							style={{minWidth: 160}}
							onChange={(bankValue) => handleInlineSave(item.id, { payment: 'bank', bankAccount: bankValue })}
							onBlur={() => setPendingBank(null)}
						/>
					</div>
				);
			}

			if (canEditInline && isEditing) {
				return (
					<Select
						autoFocus
						size="small"
						defaultValue={item.payment}
						options={paymentOptions}
						style={{minWidth: 130}}
						onChange={(value) => {
							if (value === 'bank') {
								setEditingCell(null);
								setPendingBank({ id: item.id });
							} else {
								handleInlineSave(item.id, { payment: value, bankAccount: null });
							}
						}}
						onBlur={() => setEditingCell(null)}
					/>
				);
			}

			const payOpt = paymentOptions.find(o => o.value === item.payment);
			return (
				<div
					style={canEditInline ? {cursor: 'pointer', minHeight: '24px'} : {}}
					title={canEditInline ? 'Nhấn để chỉnh sửa' : ''}
					onClick={() => canEditInline && setEditingCell({id: item.id, field: 'payment'})}
				>
					{payOpt ? payOpt.label : (item.payment || '--')}
					{(item?.payment === 'bank' || item?.payment === 'swipe') && item?.bankAccount && (
						<p style={{fontSize: '12px', color: '#888', margin: 0}}>{utilities.bankAccounts?.[item.bankAccount]?.title || item.bankAccount}</p>
					)}
				</div>
			);
		}},
		{title: 'Trạng thái', dataIndex: 'status', key: "status", width: '130px', render: (_, item) => (
			<StatusLabel small type={item.status.badge}>{item.status.label}</StatusLabel>
		)},
		{ title: '#', dataIndex: 'action', key: "action", width: '150px', render: (_, item) => (
			<RevenueAction
				key={item.id}
				item={item}
				onClickInfo={handleInfoClick}
				onClickStatus={handleStatusClick}
				onClickPay={handlePayClick}
				onClickEdit={handleEditClick}
				onClickDelete={handleDeleteClick}
				onClickTransfer={handleTransferClick}
			/>
		)},
	];

	if(isMobile) {
		columns = columns.filter(function (currentValue) {
			return (currentValue.key !== 'service' && currentValue.key !== 'signing')
		})
	}

	const dataSource= items.map(row => ({
		key: row.id,
		...row
	}));

	return (
		<div className="table-wrapper">
			<div className="table-box">
				<Table columns={columns} dataSource={dataSource} scroll={{ y:tableHeight, x: 'max-content' }} pagination={false} />
			</div>
			<Pagination current={pagination.page} defaultCurrent={1} defaultPageSize={20} pageSize={pagination.limit} total={pagination.total} onChange={onPaginationChange}  showSizeChanger={false} />
		</div>
	)
}

export default RevenueTable;